import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Input, Tooltip } from 'antd';
import DataTable from '@/components/data-table';
import useRowOpen from '@/components/data-table/useRowOpen';
import { ROUTES } from '@/constants';
import { balanceText, date, money } from './format';

// The table writes the page into the address itself.
const noop = () => {};
const SORT_OF = { name: 'name', count: 'count', totalRsd: 'total', lastDate: 'lastDate' };

/** Link to a supplier's profile, keeping the company. */
const supplierProfileUrl = (company, pib) =>
  `${ROUTES.admin}/${ROUTES.suppliers}/${encodeURIComponent(pib)}?company=${encodeURIComponent(company)}`;

/**
 * The company's suppliers, one row per PIB, paged and sorted on the server.
 * Paid and overdue only where the company has payment data; book balance and
 * invoices in progress only under the ledger switch (the server leaves them
 * out otherwise).
 *
 * @param {Object} props
 * @param {string} props.company
 * @param {Object} props.list - the list query (useQuery result)
 * @param {Object} props.queryParams
 * @param {Function} props.setQueryParams
 */
export default function SuppliersList({ company, list, queryParams, setQueryParams }) {
  const { t } = useTranslation(['suppliers', 'ledgerCards']);
  const { data, isLoading, error } = list;
  const [search, setSearch] = useState(queryParams.search || '');
  useEffect(() => setSearch(queryParams.search || ''), [queryParams.search]);
  // The whole row opens the profile, as on the other lists; Ctrl/Cmd + click opens a new tab.
  const openRow = useRowOpen((row) => supplierProfileUrl(company, row.pib));

  const sortOrder = (key) =>
    queryParams.sort === SORT_OF[key] ? (queryParams.order === 'desc' ? 'descend' : 'ascend') : null;

  const columns = [
    {
      title: t('columns.name'),
      dataIndex: 'name',
      key: 'name',
      width: 300,
      sorter: true,
      sortOrder: sortOrder('name') || (!queryParams.sort ? 'ascend' : null),
      ellipsis: { showTitle: false },
      render: (name, row) => (
        <Tooltip title={name} placement="topLeft">
          <Link to={supplierProfileUrl(company, row.pib)} className="font-semibold">
            {name || row.pib}
          </Link>
        </Tooltip>
      ),
    },
    { title: t('columns.pib'), dataIndex: 'pib', key: 'pib', width: 130 },
    { title: t('columns.registrationNo'), dataIndex: 'registrationNo', key: 'registrationNo', width: 120 },
    { title: t('columns.count'), dataIndex: 'count', key: 'count', width: 100, align: 'right', sorter: true, sortOrder: sortOrder('count') },
    {
      title: t('columns.total'),
      dataIndex: 'totalRsd',
      key: 'totalRsd',
      width: 160,
      align: 'right',
      sorter: true,
      sortOrder: sortOrder('totalRsd'),
      render: money,
    },
    data?.hasPayments && { title: t('columns.paid'), dataIndex: 'paidRsd', key: 'paidRsd', width: 150, align: 'right', render: money },
    data?.hasPayments && {
      title: (
        <Tooltip title={t('columns.overdueHint')}>
          <span>{t('columns.overdue')}</span>
        </Tooltip>
      ),
      dataIndex: 'overdueCount',
      key: 'overdueCount',
      width: 120,
      align: 'right',
    },
    data?.ledger && {
      title: t('columns.bookBalance'),
      dataIndex: 'bookBalance',
      key: 'bookBalance',
      width: 170,
      align: 'right',
      render: (b) => (b ? balanceText(b, (k) => t(k, { ns: 'ledgerCards' })) : '—'),
    },
    data?.inProgressShown && {
      title: t('columns.inProgress'),
      dataIndex: 'inProgress',
      key: 'inProgress',
      width: 170,
      align: 'right',
      render: (p) => (p?.count ? `${money(p.amount)} (${p.count})` : ''),
    },
    { title: t('columns.lastDate'), dataIndex: 'lastDate', key: 'lastDate', width: 120, sorter: true, sortOrder: sortOrder('lastDate'), render: date },
  ].filter(Boolean);

  return (
    <DataTable
      rowKey="pib"
      title={`${t('listTitle')}${data ? ` · ${data.count}` : ''}`}
      data={data?.items || []}
      total={data?.count || 0}
      status={isLoading ? 'pending' : error ? 'error' : 'success'}
      columns={columns}
      offset={queryParams.offset}
      limit={queryParams.limit}
      onChangePagination={noop}
      onRow={openRow}
      onTableChange={({ sorter }) => {
        if (!sorter || Array.isArray(sorter)) return;
        const sort = sorter.order ? SORT_OF[sorter.columnKey] : undefined;
        setQueryParams({ sort, order: sorter.order === 'descend' ? 'desc' : sort ? 'asc' : undefined, offset: 1 });
      }}
      tableLayout="fixed"
      scroll={{ x: columns.reduce((s, c) => s + (c.width || 0), 0) }}
      size="small"
      headerChildren={
        <Input.Search
          allowClear
          className="max-w-[280px] [&_.ant-input-affix-wrapper]:rounded-l-full [&_.ant-input-search-button]:rounded-r-full"
          placeholder={t('search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onSearch={(v) => setQueryParams({ search: v || undefined, offset: 1 })}
        />
      }
    />
  );
}
